import styled from "@emotion/styled";
import { Icon } from "@iconify/react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Departments, maxWorkingHoursWeekly } from "../../lib/constants";
import {
  capitalizeString,
  getMonthFromYearWeek,
  getStartOfWeek,
  getWeekOfMonthFromYearWeek,
  getWeeksData,
} from "../../lib/utils";
import { useEmployee } from "../../services";
import { SelectInput } from "../Select";

function EmployeeListDropdown({ employees, setFormData, formData, disabled = false }) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedEmployeeForSchedule, setSelectedEmployeeForSchedule] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (employees && employees.length > 0)
      setEmployeeOptions(employees.map((employee) => ({ value: employee.employee_id, ...employee })));
  }, [employees]);

  const handleShowSchedule = (employeeId) => {
    const employee = employeeOptions.find((option) => option.value === employeeId);
    setSelectedEmployeeForSchedule(employee);
    setShowEmployeeModal(true);
  };

  const handleSelectEmployee = (employeeId) => {
    const employee = employeeOptions.find((option) => option.value === employeeId);
    // setSelectedEmployeeForSchedule(employee.name);
    handleShowSchedule(employeeId);
  };

  const handleScheduleSelection = (schedule, employee) => {
    setFormData((prev) => ({
      ...prev,
      employee_id: employee.employee_id,
      department: employee.department,
      scheduled: schedule,
    }));
    setShowEmployeeModal(false);
  };

  useEffect(() => {
    setSelectedEmployee(
      formData?.employee_id ? employeeOptions.find((option) => option.value === formData?.employee_id)?.name : ""
    );
  }, [formData, employeeOptions]);

  return (
    <>
      <FormControl sx={{ minWidth: "15rem" }} size="small">
        <Select
          name="employee"
          id="employee"
          onChange={(e) => handleSelectEmployee(e.target.value)}
          value={formData?.employee_id ?? ""}
          input={<SelectInput />}
          displayEmpty
          sx={{ fontSize: "0.875rem" }}
          renderValue={(selected) => (selected ? selectedEmployee : "Select employee")}
          required
          disabled={disabled}
          MenuProps={{
            sx: {
              ".MuiInputBase-root": {
                position: "relative",
                background: "red",
                "& .dropdown-icon": { color: "red" },
              },
              ".MuiList-root": { display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.5rem" },
            },
          }}
          IconComponent={() => <Icon icon="mdi:chevron-down" className="dropdown-icon" />}
        >
          {employees &&
            employees.length > 0 &&
            employees.map((employee) => (
              <MenuItem
                value={employee.employee_id}
                key={employee.employee_id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "subgrid",
                  gridColumn: "span 2",
                  fontSize: "0.875rem",
                }}
              >
                <span>{employee.name}</span>
                {employee.department === Departments.DRAWING || employee.department === Departments.DESIGN ? (
                  <div className="grid_3">
                    <span className="xs">{capitalizeString(employee.department)}</span>
                    <span className="gray">{employee.weekly_hours_available ?? maxWorkingHoursWeekly} hours left</span>
                    <button
                      onClick={() => handleShowSchedule(employee.employee_id)}
                      type="button"
                      className="btn ghost_btn"
                    >
                      view schedule
                    </button>
                  </div>
                ) : (
                  <div className="row end">
                    <button
                      onClick={() => handleShowSchedule(employee.employee_id)}
                      type="button"
                      className="btn ghost_btn no_underline"
                    >
                      <span className="underline">site-visits-in-line:</span>
                      <span> {employee.site_visits_scheduled ?? 0}</span>
                    </button>
                  </div>
                )}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <EmployeeScheduleModal
        employee={selectedEmployeeForSchedule}
        open={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        onSubmit={handleScheduleSelection}
        formData={formData}
      />
    </>
  );
}

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const EmployeeScheduleModal = ({ employee, open, onClose, onSubmit, formData }) => {
  const department = employee?.department;
  const [schedule, setSchedule] = useState([]);
  const { getEmployeeScheduleById } = useEmployee();

  const fetchSchedule = useCallback(async () => {
    const { schedule, error } = await getEmployeeScheduleById(employee.employee_id);
    if (error) {
      toast.error(error || "Something went wrong while fetching employee schedule");
    }
    setSchedule(schedule);
  }, [employee, getEmployeeScheduleById]);

  useEffect(() => {
    if (open) fetchSchedule();
  }, [fetchSchedule, open]);

  const month = new Date().getMonth();
  const year = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(month);
  const [selectedWeeks, setSelectedWeeks] = useState(getWeeksData(getStartOfWeek(new Date(year, month, 1))));
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [visibleMonthRangeStart, setVisibleMonthRangeStart] = useState(month);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleMonthChange = (direction) => {
    let newMonthRangeStart = visibleMonthRangeStart + direction * 4;
    let newYear = selectedYear;

    if (newMonthRangeStart > 11) {
      newMonthRangeStart = newMonthRangeStart % 12;
      newYear += 1;
    } else if (newMonthRangeStart < 0) {
      newMonthRangeStart = 12 + (newMonthRangeStart % 12);
      newYear -= 1;
    }

    handleSelectMonth(newMonthRangeStart);
    setVisibleMonthRangeStart(newMonthRangeStart);
    setSelectedYear(newYear);
  };

  const handleSelectMonth = (monthIndex) => {
    setSelectedMonth(monthIndex);
    const firstWeekStart = getStartOfWeek(new Date(selectedYear, monthIndex, 1));
    setSelectedWeeks(getWeeksData(firstWeekStart));
  };

  const handleSubmit = () => {
    let dataToSubmit = {};

    if (department === "site visit") {
      dataToSubmit = {
        assigned_week: selectedWeeks[selectedWeek].yearWeek,
        assigned_date: new Date(daysOfWeek[selectedDay + 1].fullDate).toISOString().split("T")[0],
        assigned_shift: selectedShift,
      };
    } else {
      dataToSubmit = { assigned_week: selectedWeeks[selectedWeek].yearWeek };
    }

    onSubmit(dataToSubmit, employee);
  };

  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedShift, setSelectedShift] = useState("");
  interface DayOfWeek { day: string; date: string; fullDate: Date; month: string; year: number; }
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>([]);

  useEffect(() => {
    if (formData?.employee_id === employee?.employee_id) {
      const assignedWeek = formData?.schedule?.assigned_week;
      if (assignedWeek) {
        const month = getMonthFromYearWeek(assignedWeek);
        const weekOfMonth = getWeekOfMonthFromYearWeek(assignedWeek);
        setSelectedMonth(month);
        setSelectedWeek(weekOfMonth - 1); // Adjusting for zero-based index
      }
      const assignedShift = formData?.schedule?.assigned_shift;
      if (assignedShift) {
        setSelectedShift(assignedShift);
      }
    }
    const currentDate = new Date();
    const startOfWeek = getStartOfWeek(currentDate);
    const daysArr = Array.from({ length: 5 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return {
        day: date.toLocaleDateString("en-US", { weekday: "short" }),
        date: date.toLocaleDateString("en-US", { day: "numeric" }),
        fullDate: date,
        month: date.toLocaleDateString("en-US", { month: "short" }),
        year: date.getFullYear(),
      };
    });
    const today = new Date().toLocaleDateString("en-US", { day: "numeric" });
    setDaysOfWeek(daysArr);
    setSelectedDay(daysArr.findIndex((day) => day.date === today));
  }, [employee, formData]);

  const renderSiteVisitContent = () => {
    if (department !== "site visit") return null;

    return (
      <div className="column small">
        <h3 className="small font-md">{employee.name} Site Visit Schedule</h3>
        <div className="row">
          <span>Select Day</span>
          <span>
            {daysOfWeek[selectedDay]?.month} {daysOfWeek[selectedDay]?.year}
          </span>
        </div>
        <div className="row days_row">
          <button
            className="btn ghost_btn"
            onClick={() => {
              const newStartDate = new Date(daysOfWeek[0].fullDate);
              newStartDate.setDate(newStartDate.getDate() - 5);
              const newDaysOfWeek = Array.from({ length: 5 }, (_, i) => {
                const date = new Date(newStartDate);
                date.setDate(newStartDate.getDate() + i);
                return {
                  day: date.toLocaleDateString("en-US", { weekday: "short" }),
                  date: date.toLocaleDateString("en-US", { day: "numeric" }),
                  fullDate: date,
                  month: date.toLocaleDateString("en-US", { month: "short" }),
                  year: date.getFullYear(),
                };
              });
              setSelectedDay(0);
              setDaysOfWeek(newDaysOfWeek);
            }}
          >
            <Icon icon="mingcute:left-line" className="icon" />
          </button>
          {daysOfWeek.map((day, index) => (
            <div className="day_wrapper" key={index}>
              <button
                className={`btn day ${selectedDay === index ? "current" : ""}`}
                onClick={() => setSelectedDay(index)}
              >
                <span>{day.date}</span>
              </button>
            </div>
          ))}
          <button
            className="btn ghost_btn"
            onClick={() => {
              const newStartDate = new Date(daysOfWeek[0].fullDate);
              newStartDate.setDate(newStartDate.getDate() + 5);
              const newDaysOfWeek = Array.from({ length: 5 }, (_, i) => {
                const date = new Date(newStartDate);
                date.setDate(newStartDate.getDate() + i);
                return {
                  day: date.toLocaleDateString("en-US", { weekday: "short" }),
                  date: date.toLocaleDateString("en-US", { day: "numeric" }),
                  fullDate: date,
                  month: date.toLocaleDateString("en-US", { month: "short" }),
                  year: date.getFullYear(),
                };
              });
              setSelectedDay(0);
              setDaysOfWeek(newDaysOfWeek);
            }}
          >
            <Icon icon="mingcute:right-line" className="icon" />
          </button>
        </div>
        {selectedDay !== null && (
          <div className="column radio_buttons">
            <div className="small font-md">Select time of the day</div>
            <div className="row start spread">
              {["First Half", "Second Half"].map((slot, index) => (
                <label key={index} className="label gray small">
                  <input
                    type="radio"
                    name="timeSlot"
                    value={slot}
                    onChange={() => setSelectedShift(slot)}
                    checked={selectedShift === slot}
                  />
                  {slot}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="row center">
          <button className="btn ghost_outline_btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn cta_btn" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
    );
  };

  const renderDrawingContent = () => {
    if (!["design", "drawing"].includes(department)) return null;

    return (
      <>
        <h3 className="text_center small font-md">{employee.name} Schedule (Select week & timeline)</h3>
        <div className="column spread small">
          <div className="column">
            <div className="row ">
              <span>Select Month</span>
              <span>Year {selectedYear}</span>
            </div>
            <div className="row month_row">
              <button
                onClick={() => handleMonthChange(-1)}
                disabled={visibleMonthRangeStart === month && selectedYear === year}
                className="btn ghost_btn"
              >
                <Icon icon="mingcute:left-line" className="icon" />
              </button>
              {months
                .slice(visibleMonthRangeStart, visibleMonthRangeStart + 4)
                .concat(visibleMonthRangeStart + 4 > 11 ? months.slice(0, (visibleMonthRangeStart + 4) % 12) : [])
                .map((monthName, index) => {
                  const actualMonthIndex = (visibleMonthRangeStart + index) % 12;
                  return (
                    <div className="month_wrapper" key={index}>
                      <button
                        key={index}
                        className={`btn month ${actualMonthIndex === selectedMonth ? "current" : ""}`}
                        onClick={() => handleSelectMonth(actualMonthIndex)}
                      >
                        {monthName}
                      </button>
                    </div>
                  );
                })}
              <button onClick={() => handleMonthChange(1)} className="btn ghost_btn">
                <Icon icon="mingcute:right-line" className="icon" />
              </button>
            </div>
          </div>
          <div className="column">
            <span>Select Week</span>
            <div className="row">
              {selectedWeeks.map((week, index) => (
                <div className="week_wrapper" key={index}>
                  <button
                    className={`btn week ${index === selectedWeek ? "current" : ""}`}
                    onClick={() => setSelectedWeek(index)}
                  >
                    {week.week}
                  </button>
                  <span className="dates">{week.dates}</span>
                  <span className="sm">
                    ({week.yearWeek === (schedule as any).assigned_week ? (schedule as any).alloted_hours : maxWorkingHoursWeekly}
                    {" hrs available"})
                  </span>
                </div>
                // <div className="week_wrapper" key={index}>
                //   <button
                //     className={`btn week ${index === selectedWeeks ? "current" : ""}`}
                //     onClick={() => setSelectedWeeks(index)}
                //   >
                //     {week}
                //   </button>
                //   <span className="dates">{week.dates}</span>
                // </div>
              ))}
            </div>
          </div>
          <div className="row center">
            <button className="btn ghost_outline_btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn cta_btn" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      aria-labelledby="employee-schedule-modal"
      aria-describedby="employee-schedule-modal"
    >
      <div className="modal_container fit column">
        {renderSiteVisitContent()}
        {renderDrawingContent()}
      </div>
    </ModalWrapper>
  );
};

export default EmployeeListDropdown;
